(() => {
  'use strict';

  if (window.TSSOrderCheckoutGateInitialised) {
    return;
  }

  window.TSSOrderCheckoutGateInitialised = true;

  const shopRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const checkoutSelector = [
    '.cart-form [name="checkout"]',
    'form[action*="/cart"] [name="checkout"]',
    'a[href*="/checkout"]'
  ].join(',');

  let modal = null;
  let previouslyFocusedElement = null;
  let checkingCart = false;
  let allowNextSubmit = false;

  function wait(milliseconds) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });
  }

  async function retrieveCart() {
    const response = await fetch(
      `${shopRoot}cart.js`,
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to retrieve cart: ${response.status}`
      );
    }

    return response.json();
  }

  function orderInformationIsComplete(cart) {
    const attributes =
      cart && cart.attributes
        ? cart.attributes
        : {};

    const method =
      attributes['Order fulfilment method'];

    if (method === 'Delivery') {
      return (
        attributes[
          'Delivery information complete'
        ] === 'Yes'
      );
    }

    if (method === 'Pick up') {
      return (
        attributes[
          'Pickup information complete'
        ] === 'Yes'
      );
    }

    return false;
  }

  function createModal() {
    if (modal) {
      return modal;
    }

    const style = document.createElement('style');

    style.textContent = `
      body.tss-order-gate-open {
        overflow: hidden;
      }

      .tss-order-gate[hidden] {
        display: none !important;
      }

      .tss-order-gate {
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: 18px;
        background: rgba(0, 0, 0, 0.58);
      }

      .tss-order-gate__dialog {
        box-sizing: border-box;
        width: min(500px, 100%);
        max-height: calc(100vh - 36px);
        overflow-y: auto;
        padding: 24px;
        border: 2px solid #00aeef;
        border-radius: 10px;
        background: #ffffff;
        color: #34383b;
        box-shadow: 0 18px 55px rgba(0, 0, 0, 0.3);
        text-align: center;
      }

      .tss-order-gate__title {
        margin: 0 0 10px;
        font-size: 22px;
        line-height: 1.25;
      }

      .tss-order-gate__text {
        margin: 0 0 10px;
        font-size: 15px;
        line-height: 1.5;
      }

      .tss-order-gate__next {
        margin: 0 0 18px;
        font-size: 13px;
        line-height: 1.4;
        opacity: 0.8;
      }

      .tss-order-gate__button {
        box-sizing: border-box;
        width: 100%;
        min-height: 46px;
        padding: 10px 15px;
        border: 1px solid #00aeef;
        border-radius: 6px;
        background: #00aeef;
        color: #ffffff;
        font: inherit;
        font-weight: 700;
        line-height: 1.3;
        cursor: pointer;
      }

      .tss-order-gate__button:hover {
        opacity: 0.92;
      }

      .tss-order-gate__button:focus-visible {
        outline: 3px solid rgba(0, 174, 239, 0.35);
        outline-offset: 3px;
      }

      .tss-order-gate-highlight {
        outline: 3px solid rgba(0, 174, 239, 0.42);
        outline-offset: 5px;
        transition: outline-color 300ms ease;
      }

      @media screen and (max-width: 480px) {
        .tss-order-gate {
          padding: 13px;
        }

        .tss-order-gate__dialog {
          padding: 20px 16px;
        }

        .tss-order-gate__title {
          font-size: 20px;
        }
      }
    `;

    document.head.appendChild(style);

    modal = document.createElement('div');
    modal.className = 'tss-order-gate';
    modal.hidden = true;

    modal.innerHTML = `
      <div
        class="tss-order-gate__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tss-order-gate-title"
        aria-describedby="tss-order-gate-description"
      >
        <h2
          id="tss-order-gate-title"
          class="tss-order-gate__title"
        >
          Required order information
        </h2>

        <p
          id="tss-order-gate-description"
          class="tss-order-gate__text"
        >
          Before checkout, select Delivery or Pick up,
          complete the information shown and click
          Save order information.
        </p>

        <p class="tss-order-gate__next">
          After saving, select Checkout to continue.
        </p>

        <button
          type="button"
          class="tss-order-gate__button"
          data-tss-complete-order-information
        >
          Complete required order information
        </button>
      </div>
    `;

    document.body.appendChild(modal);

    modal
      .querySelector(
        '[data-tss-complete-order-information]'
      )
      .addEventListener('click', () => {
        closeModal(false);

        /*
         * Wait until the popup and page-scroll lock
         * are fully removed before calculating the
         * form's position.
         */
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            focusOrderInformation();
          });
        });
      });

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          modal &&
          !modal.hidden
        ) {
          closeModal(true);
        }
      }
    );

    return modal;
  }

  function openModal() {
    createModal();

    previouslyFocusedElement =
      document.activeElement;

    modal.hidden = false;

    document.body.classList.add(
      'tss-order-gate-open'
    );

    const button = modal.querySelector(
      '[data-tss-complete-order-information]'
    );

    if (button) {
      button.focus();
    }
  }

  function closeModal(restoreFocus) {
    if (!modal) {
      return;
    }

    modal.hidden = true;

    document.body.classList.remove(
      'tss-order-gate-open'
    );

    if (
      restoreFocus &&
      previouslyFocusedElement instanceof
        HTMLElement
    ) {
      previouslyFocusedElement.focus();
    }
  }

  function calculatePinnedHeaderOffset() {
    return Array.from(
      document.querySelectorAll(
        [
          'header',
          '[role="banner"]',
          '#shopify-section-header',
          '.shopify-section-group-header-group'
        ].join(',')
      )
    ).reduce((largestOffset, element) => {
      const style =
        window.getComputedStyle(element);

      const rect =
        element.getBoundingClientRect();

      const isPinned =
        style.position === 'fixed' ||
        style.position === 'sticky';

      const isNearTop =
        rect.bottom > 0 &&
        rect.top < 180 &&
        rect.bottom <
          window.innerHeight * 0.55;

      return isPinned && isNearTop
        ? Math.max(
            largestOffset,
            rect.bottom + 18
          )
        : largestOffset;
    }, 18);
  }

  function focusOrderInformation() {
    const form = document.querySelector(
      '[data-tss-delivery-information]'
    );

    if (!form) {
      return;
    }

    const targetTop =
      window.scrollY +
      form.getBoundingClientRect().top -
      calculatePinnedHeaderOffset();

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth'
    });

    form.classList.add(
      'tss-order-gate-highlight'
    );

    window.setTimeout(() => {
      form.classList.remove(
        'tss-order-gate-highlight'
      );
    }, 2200);

    window.setTimeout(() => {
      const methodInputs = Array.from(
        form.querySelectorAll(
          '[data-tss-order-method]'
        )
      );

      const addressTypeInputs = Array.from(
        form.querySelectorAll(
          '[data-tss-address-type]'
        )
      );

      const authorityInputs = Array.from(
        form.querySelectorAll(
          '[data-tss-atl]'
        )
      );

      const atlLocation = form.querySelector(
        '[data-tss-atl-location]'
      );

      const buildingSignage =
        form.querySelector(
          '[data-tss-building-signage]'
        );

      const pickupConfirmation =
        form.querySelector(
          '[data-tss-pickup-confirmation]'
        );

      const saveButton = form.querySelector(
        '[data-tss-delivery-save]'
      );

      const selectedMethod =
        methodInputs.find(
          (input) => input.checked
        )?.value || '';

      const selectedAddressType =
        addressTypeInputs.find(
          (input) => input.checked
        )?.value || '';

      const selectedAuthority =
        authorityInputs.find(
          (input) => input.checked
        )?.value || '';

      let target = null;

      if (!selectedMethod) {
        target = methodInputs[0];
      } else if (
        selectedMethod === 'Delivery'
      ) {
        if (!selectedAddressType) {
          target = addressTypeInputs[0];
        } else if (!selectedAuthority) {
          target = authorityInputs[0];
        } else if (
          selectedAuthority === 'Yes' &&
          atlLocation &&
          atlLocation.value.trim() === ''
        ) {
          target = atlLocation;
        } else if (
          selectedAddressType === 'Business' &&
          buildingSignage &&
          buildingSignage.value.trim() === ''
        ) {
          target = buildingSignage;
        } else {
          target = saveButton;
        }
      } else if (
        selectedMethod === 'Pick up'
      ) {
        if (
          pickupConfirmation &&
          !pickupConfirmation.checked
        ) {
          target = pickupConfirmation;
        } else {
          target = saveButton;
        }
      }

      if (
        target &&
        typeof target.focus === 'function'
      ) {
        target.focus({
          preventScroll: true
        });
      }
    }, 400);
  }

  function findVisibleCheckoutButton() {
    return Array.from(
      document.querySelectorAll(
        checkoutSelector
      )
    ).find((element) => {
      const style =
        window.getComputedStyle(element);

      const rect =
        element.getBoundingClientRect();

      const isDisabled =
        element.matches(
          '[disabled], [aria-disabled="true"]'
        );

      return (
        !isDisabled &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        rect.width > 0 &&
        rect.height > 0
      );
    });
  }

  function focusCheckoutButton() {
    const checkoutButton =
      findVisibleCheckoutButton();

    if (!checkoutButton) {
      return;
    }

    checkoutButton.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    checkoutButton.classList.add(
      'tss-order-gate-highlight'
    );

    window.setTimeout(() => {
      checkoutButton.classList.remove(
        'tss-order-gate-highlight'
      );
    }, 2200);

    window.setTimeout(() => {
      if (
        typeof checkoutButton.focus ===
          'function'
      ) {
        checkoutButton.focus({
          preventScroll: true
        });
      }
    }, 450);
  }

  function continueToCheckout(
    checkoutTrigger
  ) {
    if (
      !checkoutTrigger ||
      !document.documentElement.contains(
        checkoutTrigger
      )
    ) {
      window.location.assign(
        `${shopRoot}checkout`
      );

      return;
    }

    if (
      checkoutTrigger.matches(
        'a[href*="/checkout"]'
      )
    ) {
      window.location.assign(
        checkoutTrigger.href
      );

      return;
    }

    const form =
      checkoutTrigger.form ||
      checkoutTrigger.closest('form');

    if (
      form &&
      typeof form.requestSubmit ===
        'function'
    ) {
      allowNextSubmit = true;

      try {
        form.requestSubmit(
          checkoutTrigger
        );

        window.setTimeout(() => {
          allowNextSubmit = false;
        }, 1200);

        return;
      } catch (error) {
        allowNextSubmit = false;
      }
    }

    window.location.assign(
      `${shopRoot}checkout`
    );
  }

  async function handleCheckout(
    checkoutTrigger
  ) {
    if (checkingCart) {
      return;
    }

    checkingCart = true;

    if (
      checkoutTrigger instanceof
        HTMLElement
    ) {
      checkoutTrigger.setAttribute(
        'aria-busy',
        'true'
      );
    }

    try {
      /*
       * Allow any delayed completion reset to finish
       * before checking the cart's saved state.
       */
      await wait(450);

      const cart = await retrieveCart();

      if (
        orderInformationIsComplete(cart)
      ) {
        continueToCheckout(
          checkoutTrigger
        );

        return;
      }

      openModal();
    } catch (error) {
      console.warn(
        'Required order information could not be verified.',
        error
      );

      /*
       * Fail safely instead of allowing an
       * unverified order through the cart.
       */
      openModal();
    } finally {
      if (
        checkoutTrigger instanceof
          HTMLElement
      ) {
        checkoutTrigger.removeAttribute(
          'aria-busy'
        );
      }

      checkingCart = false;
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      if (
        !document.querySelector(
          '[data-tss-delivery-information]'
        )
      ) {
        return;
      }

      if (
        !(event.target instanceof Element)
      ) {
        return;
      }

      const checkoutTrigger =
        event.target.closest(
          checkoutSelector
        );

      if (!checkoutTrigger) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      handleCheckout(
        checkoutTrigger
      );
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (allowNextSubmit) {
        allowNextSubmit = false;
        return;
      }

      if (
        !document.querySelector(
          '[data-tss-delivery-information]'
        )
      ) {
        return;
      }

      const submitter =
        event.submitter;

      if (
        !(submitter instanceof Element) ||
        !submitter.matches(
          '[name="checkout"]'
        )
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      handleCheckout(submitter);
    },
    true
  );

  /*
   * Saving never starts checkout automatically.
   * In every scenario, move the customer to the
   * normal Checkout button and require them to
   * click it themselves.
   */
  document.addEventListener(
    'tss:order-information-saved',
    () => {
      window.setTimeout(() => {
        focusCheckoutButton();
      }, 500);
    }
  );
})();